<script setup lang="ts">
/**
 * SeoCardSticker — one card in a /karty grid, as a sticker in an album (the Codex
 * look): the real face with a cream die-cut ring + soft lift (box-shadow only — it
 * sits in grids of up to 78), a slight tilt seeded by its index (SSR-stable), the
 * card's number on a tiny cream tag over the corner, the name underneath.
 * The image is lazy unless `priority` (the first row above the fold).
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { cardImagePath, cardImageSrc } from '@app/data/cardSeoData';

const props = withDefaults(
  defineProps<{
    slug: string;
    name: string;
    number: string;
    /** Position in its grid — seeds the tilt. */
    index?: number;
    /** Eager + high fetch priority (first row). */
    priority?: boolean;
  }>(),
  { index: 0, priority: false },
);

const TILTS = [-1.1, 0.7, -0.4, 0.9, -0.8, 0.5, 1, -0.6, 0.3];
const tilt = computed(() => `${TILTS[props.index % TILTS.length]}deg`);
const src = computed(() => cardImageSrc(props.slug));
</script>

<template>
  <RouterLink
    :to="`/karty/${slug}`"
    class="s-link flex flex-col items-center gap-2.5 rounded-[var(--paper-radius-sm)]"
  >
    <span
      class="s-sticker relative block w-full"
      :style="{ '--tilt': tilt }"
    >
      <picture class="contents">
        <source
          type="image/avif"
          :srcset="src.avif"
        >
        <source
          type="image/webp"
          :srcset="src.webp"
        >
        <img
          :src="cardImagePath(slug)"
          :alt="`Карта Таро ${name}`"
          width="275"
          height="483"
          :loading="priority ? 'eager' : 'lazy'"
          :fetchpriority="priority ? 'high' : undefined"
          decoding="async"
          class="s-art aspect-[275/483] w-full object-cover"
        >
      </picture>
      <span class="s-num paper-tag type-overline">{{ number }}</span>
    </span>
    <h3 class="type-label line-clamp-2 px-0.5 text-center text-foreground">{{ name }}</h3>
  </RouterLink>
</template>

<style scoped>
/* The sticker: cream die-cut ring + a soft lift shadow (TarotCard's face). */
.s-sticker {
  rotate: var(--tilt, 0deg);
  border-radius: 7px;
  background: var(--paper-stock);
  box-shadow:
    0 0 0 3px var(--paper-stock),
    0 8px 14px -4px var(--paper-shade),
    0 1px 3px var(--paper-shade);
  transition:
    translate 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.2s ease-out;
}
.s-art {
  display: block;
  border-radius: 5px;
  background: var(--muted);
}
@media (hover: hover) {
  .s-link:hover .s-sticker {
    translate: 0 -3px;
    box-shadow:
      0 0 0 3px var(--paper-stock),
      0 12px 18px -6px var(--paper-shade),
      0 1px 3px var(--paper-shade);
  }
}
.s-link:active .s-sticker {
  translate: 0 1px;
}
.s-link:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 4px;
}
/* The card's number on a tiny cream tag, stuck over the top-left corner. */
.s-num {
  position: absolute;
  top: -6px;
  left: -6px;
  min-width: 22px;
  padding: 1px 5px;
  text-align: center;
  letter-spacing: 0.04em;
  border-radius: var(--paper-radius-xs);
  rotate: -4deg;
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.5), 0 2px 4px -1px var(--paper-shade);
}
@media (prefers-reduced-motion: reduce) {
  .s-sticker {
    transition: none;
  }
}
</style>
